import { BrButton, BrDivider, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import CustomConfirmer from 'components/customConfirmer/customConfirmer';
import CustomDivider from 'components/customDivider/customDivider';
import CustomStep from 'components/customStep/customStep';
import ErrorMessage from 'components/errorMessage/errorMessage';
import ModalOpcoesPagamento from 'components/modalOpcoesPagamento/modalOpcoesPagamento';
import StepFormAccordionConfig from 'components/stepFormAccordionConfig/stepFormAccordionConfig';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { useNotificacao } from 'hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { INovaGuiaRecolhimento } from 'interfaces/IGuiaRecolhimento';
import ResumoSection from 'pages/components/resumoSection/resumoSection';
import { useContext, useEffect, useState } from 'react';
import { Button, Modal } from 'react-dsgov';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { formatarDataBRpISO } from 'utils/formatDate';
import TituloSection from '../../../components/tituloSection/tituloSection';
import { ICrumb } from '../../../interfaces/ICrumb';
import { useBackEndRequests } from '../lib/backEndRequests';
import DadosOperacoes from './components/dadosOperacoes/dadosOperacoes';
import { validarDadosRecolhimento } from './components/dadosRecolhimento/dadoRecolhimentoValidacoes';
import DadosRecolhimento from './components/dadosRecolhimento/dadosRecolhimento';
import styles from './PrimeiroAdquirenteEdit.module.scss';

interface IPrimeiroAdquirenteEditProps {
  stepProp?: number;
}

function PrimeiroAdquirenteEdit({ stepProp }: Readonly<IPrimeiroAdquirenteEditProps>) {
  const [step, setStep] = useState(stepProp || 1);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isModalOpcoesPagamentoOpened, setIsModalOpcoesPagamentoOpened] = useState(false);
  const [isEdicaoDadosRecolhimento, setIsEdicaoDadosRecolhimento] = useState(false);
  const [opcaoPagamento, setOpcaoPagamento] = useState('');
  const [isProcessoVencido, setIsProcessoVencido] = useState(false);

  const [isLoadingSalvarDadosRecolhimento, setIsLoadingSalvarDadosRecolhimento] = useState(false);
  const [isLoadingCopiarGuia, setIsLoadingCopiarGuia] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const userCpfCnpj = sessionStorage.getItem('userCpfCnpj');

  const [isEdicaoComponente, setIsEdicaoComponente] = useState<boolean>(false);

  const navigate = useNavigate();
  const {
    postGuiaRecolhimento,
    putGuiaRecolhimento,
    deleteGuiaRecolhimento,
    getPermissaoAcessarGuiaRecolhimento,
    getCopiarGuia,
    getDadosRecolhimento,
  } = useBackEndRequests();

  const [dadosRecolhimento, setDadosRecolhimento] = useState<INovaGuiaRecolhimento>({
    id: guiaRecolhimentoId,
    tipoDeclarante: 1,
    cpfCnpjDeclarante: userCpfCnpj || '',
    competenciaMes: 0,
    competenciaAno: 0,
    numeroProcesso: '',
    substancia: '',
    municipio: -1,
    tipoRelacao: 0,
    observacoes: '',
    dataSolicitacaoRenovacaoPlg: '',
  });

  const context = useContext(ArrecadacaoContext);
  const mostrarNotificacao = useNotificacao();

  const steps = [
    { label: 'Informações do emitente' },
    { label: 'Dados do recolhimento' },
    { label: 'Dados das operações' },
    { label: 'Guia de recolhimento' },
    { label: 'Resumo' },
  ];

  const {
    data: permissaoStatus,
    isLoading: isLoadingPermissaoAcesso,
    error: permissaoAcessoError,
    refetch: permissaoAcessoRefetch,
  } = useQuery<number, IErrorWithSentryId>({
    queryKey: ['getPermissaoAcessarGuiaRecolhimento', guiaRecolhimentoId, userCpfCnpj],
    queryFn: () => getPermissaoAcessarGuiaRecolhimento(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  useEffect(() => {
    const crumbs: ICrumb[] = [
      {
        isHome: true,
        label: 'home',
        onClick: () => {
          navigate('/');
        },
      },
      {
        label: 'Primeiro Adquirente',
      },
      {
        active: true,
        label: 'Recolhimento da CFEM',
      },
    ];
    context?.setCrumbs(crumbs);

    const isPrimeiroAdquirente = sessionStorage.getItem('isPrimeiroAdquirente') === 'true';
    const isPessoaNaoCadastrada = sessionStorage.getItem('isPessoaNaoCadastrada') === 'true';
    const isFilial = sessionStorage.getItem('naturezaDocumentacao') === 'filial';
    if (!isPrimeiroAdquirente || isPessoaNaoCadastrada || isFilial) {
      navigate('/');
    }
  }, [navigate]);

  const enviarDadosRecolhimento = async () => {
    setIsLoadingSalvarDadosRecolhimento(true);
    if (dadosRecolhimento.id !== guiaRecolhimentoId)
      setDadosRecolhimento((prev) => ({ ...prev, id: guiaRecolhimentoId }));

    if (dadosRecolhimento.id === '' || dadosRecolhimento.cpfCnpjDeclarante === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Ocorreu um erro ao iniciar o recolhimento. Por favor, tente novamente mais tarde.',
      });
    } else {
      const msgErroValidacao = validarDadosRecolhimento(
        dadosRecolhimento,
        isEdicaoDadosRecolhimento,
        isProcessoVencido
      );

      if (msgErroValidacao === '') {
        if (isEdicaoDadosRecolhimento) {
          await putGuiaRecolhimento(dadosRecolhimento)
            .then(async () => {
              mostrarNotificacao({
                tipo: 'success',
                conteudo: 'Guia de Recolhimento salva com sucesso',
              });
              setStep(step + 1);
            })
            .catch((err) => {
              mostrarNotificacao({
                tipo: 'danger',
                conteudo: err?.response?.data?.message,
                ...(err && { complementoSentryId: err }),
              });
            })
            .finally(() => {
              setIsLoadingSalvarDadosRecolhimento(false);
            });
        } else {
          await postGuiaRecolhimento(dadosRecolhimento)
            .then(async () => {
              mostrarNotificacao({
                tipo: 'success',
                conteudo: 'Guia de Recolhimento iniciada com sucesso',
              });
              setStep(step + 1);
              context?.setNumeroProcesso(dadosRecolhimento.numeroProcesso);
            })
            .catch((err) => {
              mostrarNotificacao({
                tipo: 'danger',
                conteudo: err?.response?.data?.message,
                ...(err && { complementoSentryId: err }),
              });
            })
            .finally(() => {
              setIsLoadingSalvarDadosRecolhimento(false);
            });
        }
      } else {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: msgErroValidacao,
        });
        setIsLoadingSalvarDadosRecolhimento(false);
      }
    }
  };

  const selecionarOpcaoPagamento = () => {
    setIsModalOpcoesPagamentoOpened(true);
  };

  const avancarFinalizarAction = () => {
    switch (step) {
      case 2:
        enviarDadosRecolhimento();
        break;
      case 4:
        selecionarOpcaoPagamento();
        break;
      default:
        setStep(step + 1);
        break;
    }
  };

  const excluirGuiaRecolhimento = async () => {
    await deleteGuiaRecolhimento(guiaRecolhimentoId)
      .then(() => {
        setTimeout(() => {
          mostrarNotificacao({
            tipo: 'success',
            conteudo: 'Guia de recolhimento excluída com sucesso',
          });
        }, 100);

        window.scrollTo(0, 0);
        navigate('/');
      })
      .catch((err) => {
        setIsModalOpened(false);
        if (err.status === 404) {
          navigate('/');
        } else {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao excluir a guia de recolhimento, tente novamente mais tarde. Erro: ' + err.message,
          });
        }
      });
  };

  const copiarGuiaRecolhimento = async (isReemissao?: boolean) => {
    setIsLoadingCopiarGuia(true);
    await getCopiarGuia(guiaRecolhimentoId)
      .then((res) => {
        const novaGuiaId = res;
        setTimeout(() => {
          mostrarNotificacao({
            tipo: 'success',
            conteudo: 'Guia de recolhimento copiada com sucesso',
          });
        }, 100);
        if (isReemissao) {
          reemitirGuiaRecolhimento(res);
          return;
        }
        window.scrollTo(0, 0);
        setDadosRecolhimento((prev) => ({
          ...prev,
          id: guiaRecolhimentoId,
          tipoDeclarante: 1,
          cpfCnpjDeclarante: userCpfCnpj || '',
          competenciaMes: 0,
          competenciaAno: 0,
          numeroProcesso: '',
          substancia: '',
          municipio: -1,
          observacoes: '',
        }));
        setStep(2);
        setIsLoadingCopiarGuia(false);
        navigate(`/primeiro-adquirente?id=${novaGuiaId}`);
      })
      .catch((err) => {
        setIsLoadingCopiarGuia(false);
        if (err.status === 404) {
          navigate('/');
        } else {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao copiar a guia de recolhimento, tente novamente mais tarde. Erro: ' + err.message,
          });
        }
      });
  };

  const reemitirGuiaRecolhimento = async (novaGuiaId: string) => {
    await getDadosRecolhimento(novaGuiaId)
      .then((res) => {
        setDadosRecolhimento({
          id: res.id,
          tipoDeclarante: ([0, 1, 2].includes(Number(res.tipoDeclarante)) ? Number(res.tipoDeclarante) : 0) as
            | 0
            | 1
            | 2,
          cpfCnpjDeclarante: userCpfCnpj || '',
          competenciaMes: res.competenciaMes,
          competenciaAno: res.competenciaAno,
          numeroProcesso: res.numeroProcesso,
          substancia: res.substancia,
          municipio: res.municipio.codigoIBGE,
          dataSolicitacaoRenovacaoPlg: formatarDataBRpISO(res.dataSolicitacaoRenovacaoPlg || ''),
          tipoRelacao: res.tipoRelacao,
          tipoRecolhimento: -1,
          observacoes: res.observacoes,
        });
        setStep(4);
        setIsLoadingCopiarGuia(false);
        navigate(`/primeiro-adquirente?id=${novaGuiaId}`);
      })
      .catch((err) => {
        setIsLoadingCopiarGuia(false);
        mostrarNotificacao({
          tipo: 'danger',
          conteudo:
            'Erro ao consultar a guia de recolhimento reemitida, consulte a guia na listagem e continue a operação. Erro: ' +
            err.message,
        });
        navigate(`/`);
      });
    return;
  };

  return (
    <>
      <Modal title='Excluir Guia de Recolhimento' useScrim modalOpened={isModalOpened}>
        <Modal.Body>
          Deseja mesmo deletar <strong>TODA</strong> a guia de recolhimento? Esse processo{' '}
          <strong>não tem volta</strong> e excluirá todas as bases de cálculo vinculadas a essa guia.
        </Modal.Body>
        <Modal.Footer justify-content='end'>
          <Button secondary small m={2} onClick={() => setIsModalOpened(false)}>
            Cancelar
          </Button>
          <CustomConfirmer asyncCall={() => excluirGuiaRecolhimento()}>Confirmar</CustomConfirmer>
        </Modal.Footer>
      </Modal>
      <ModalOpcoesPagamento
        title='Meios de pagamento'
        isModalOpened={isModalOpcoesPagamentoOpened}
        setIsModalOpened={setIsModalOpcoesPagamentoOpened}
        setStep={setStep}
        step={step}
        opcaoPagamento={opcaoPagamento}
        setOpcaoPagamento={setOpcaoPagamento}
        maxStep={steps.length}
      />
      <BrDivider className={styles.divider} />
      <TituloSection type='primeiro-adquirente' isSteps />
      <CustomDivider />
      {isLoadingPermissaoAcesso && (
        <div className={styles.containerPrincipal}>
          <div className={styles.containerAvulso}>
            <BrLoading large />
          </div>
        </div>
      )}
      {!isLoadingPermissaoAcesso && permissaoAcessoError && permissaoAcessoError?.status !== 404 && (
        <div className={styles.containerPrincipal}>
          <div className={styles.containerAvulso}>
            <ErrorMessage
              errorDescription={
                permissaoAcessoError?.status === 403
                  ? 'Essa guia de recolhimento não pertence ao CPF/CNPJ selecionado, por favor verifique.'
                  : 'Erro ao buscar guia de recolhimento, tente novamente clicando no botão abaixo.'
              }
              errorMessage={permissaoAcessoError?.message || ''}
              error={permissaoAcessoError}
              errorTitle={
                permissaoAcessoError?.status === 403 ? 'Acesso Negado' : 'Erro ao buscar guia de recolhimento'
              }
              buttonMessage={permissaoAcessoError?.status === 403 ? 'Retornar à lista de guias' : 'Tentar novamente'}
              onClickFunction={
                permissaoAcessoError?.status === 403 ? () => navigate('/') : () => permissaoAcessoRefetch()
              }
              noBorder
            />
          </div>
        </div>
      )}
      {!isLoadingPermissaoAcesso && (permissaoAcessoError?.status === 404 || permissaoStatus === 202) && (
        <div className={styles.containerPrincipal}>
          <div className={styles.stepContainer}>
            <CustomStep steps={steps} setStepFormulario={setStep} stepAtual={step} />
          </div>

          <div className={styles.contentContainer}>
            <div className={styles.formContainer}>
              <div className={styles.titleContainer}>
                <h4 className={styles.title}>Nova guia de recolhimento da CFEM</h4>
                <BrButton
                  secondary
                  className={styles.buttonHelp}
                  size='small'
                  icon='fas fa-list'
                  data-tooltip-id='button-retornar-lista-guias'
                  onClick={() => navigate('/')}
                >
                  Retornar a Lista de Guias
                </BrButton>
                <Tooltip id='button-retornar-lista-guias' place='bottom-end' className={styles.tooltip} variant='info'>
                  <div className={styles.tooltipText}>
                    Essa ação retornará à lista de guias sem excluir a guia atual, mantendo-a em rascunho.
                  </div>
                </Tooltip>
              </div>
              <StepFormAccordionConfig
                key={guiaRecolhimentoId}
                step={step}
                setStep={setStep}
                stepDoisContent={
                  <DadosRecolhimento
                    isViewOnly={step !== 2}
                    dadosRecolhimento={dadosRecolhimento}
                    setDadosRecolhimento={setDadosRecolhimento}
                    isEdicaoDadosRecolhimento={isEdicaoDadosRecolhimento}
                    setIsEdicaoDadosRecolhimento={setIsEdicaoDadosRecolhimento}
                    stepAtual={step}
                  />
                }
                stepTresContent={
                  <DadosOperacoes
                    isViewOnly={step !== 3}
                    isEdicao={isEdicaoComponente}
                    setIsEdicao={setIsEdicaoComponente}
                  />
                }
                stepQuatroContent={
                  <ResumoSection
                    step={step}
                    opcaoPagamento={opcaoPagamento}
                    setIsModalOpcoesPagamentoOpened={setIsModalOpcoesPagamentoOpened}
                    isPrimeiroAdquirente={true}
                    copiarGuiaRecolhimento={copiarGuiaRecolhimento}
                  />
                }
              />
            </div>
            {step !== 5 && !isEdicaoComponente && (
              <div className={styles.buttonContainer}>
                <div className={styles.leftButtons}>
                  <BrButton className={styles.botao} icon='trash' onClick={() => setIsModalOpened(true)} secondary>
                    Excluir
                  </BrButton>
                </div>
                <div className={styles.rightButtons}>
                  {step !== 1 && (
                    <BrButton
                      className={styles.botao}
                      icon='arrow-left'
                      onClick={() => {
                        setStep(step - 1);
                        window.scrollTo(0, 300);
                      }}
                    >
                      Voltar
                    </BrButton>
                  )}

                  <BrButton
                    primary
                    className={styles.botao}
                    icon={step === 4 ? 'donate' : 'arrow-right'}
                    onClick={() => avancarFinalizarAction()}
                    loading={isLoadingSalvarDadosRecolhimento}
                  >
                    {step === 4 ? 'Finalizar e Emitir Guia de Recolhimento' : 'Avançar'}
                  </BrButton>
                </div>
              </div>
            )}
            {step === 5 && (
              <div className={styles.buttonContainer}>
                <div className={styles.leftButtons}></div>
                <div className={styles.rightButtons}>
                  <BrButton
                    primary
                    className={styles.botao}
                    icon='fas fa-edit'
                    onClick={() => copiarGuiaRecolhimento()}
                    loading={isLoadingCopiarGuia}
                  >
                    Copiar dados da Guia
                  </BrButton>

                  <BrButton primary className={styles.botao} onClick={() => navigate('/')}>
                    Ir para a listagem de guias
                  </BrButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default PrimeiroAdquirenteEdit;
